/**
 * Testing-service type definitions.
 *
 * REST response types (TestPlan, TestRun, ChatSession, etc.) are
 * auto-generated from the service's openapi.json — do NOT edit them here.
 * To regenerate run:  pnpm codegen:types   (in ncoder-ai root)
 *
 * SSE event types (TestingChatEvent, ExecuteStreamEvent) are defined
 * manually below because OpenAPI does not describe streaming event payloads.
 */

// ─── Re-exports from generated types ─────────────────────────────────────────

export type { components, paths } from "./generated";
import type { components } from "./generated";

export type StepResult = components["schemas"]["StepResult"];
export type ChatSession = components["schemas"]["ChatSession"];
export type ChatMessage = components["schemas"]["ChatMessage"];
export type PlanBody = components["schemas"]["PlanBody"];
export type IrPlanBody = components["schemas"]["IrPlanBody"];
export type SpecPlanBody = components["schemas"]["SpecPlanBody"];
export type PlanStatus = components["schemas"]["PlanStatus"];
export type PlanVisibility = components["schemas"]["PlanVisibility"];
export type RunStatus = components["schemas"]["RunStatus"];
export type TestRun = components["schemas"]["TestRun"];
export type RecentChangesResponse = components["schemas"]["RecentChangesResponse"];
export type AffectedPlansResponse = components["schemas"]["AffectedPlansResponse"];

// Bucket key union derived from the response shape — no duplication
export type RecentChangeBucket = keyof RecentChangesResponse["buckets"];

/**
 * A single destructive step the planner detected in a plan. Non-empty
 * `destructive_actions` means running the plan will DELETE records or EDIT an
 * EXISTING record — the run must be confirmed first. `label` is server-authored
 * and shown to the user verbatim (e.g. "Delete Account QA_ACC_001").
 */
export interface DestructiveAction {
  kind: "delete" | "edit";
  label: string;
}

// TestPlan — status widened to include legacy DB values from the Python service era.
// New plans created by the TS service only have the generated PlanStatus values.
export type TestPlan = Omit<components["schemas"]["TestPlan"], "status"> & {
  status: components["schemas"]["PlanStatus"] | "passed" | "failed" | "edited";
  /**
   * Destructive steps this plan performs. Empty/absent = SAFE (create/verify) →
   * run with zero friction. Non-empty → gate the RUN behind a confirmation.
   * Carried on the plan_proposed event and persisted on the plan record.
   */
  destructive_actions?: DestructiveAction[];
};

// ─── Plan body type guards ────────────────────────────────────────────────────

/** True when the plan was created by the TypeScript IR planning agent. */
export function isIrPlan(steps: PlanBody): steps is IrPlanBody {
  return !Array.isArray(steps) && (steps as { format?: unknown }).format === "ir_v1";
}

/** True when the plan has a directly executable Playwright spec. */
export function isSpecPlan(steps: PlanBody): steps is SpecPlanBody {
  return !Array.isArray(steps) && typeof (steps as { spec_ts?: unknown }).spec_ts === "string";
}

/** True when the plan has any executable body (spec, healed spec, or IR). */
export function isExecutablePlan(steps: PlanBody): boolean {
  if (Array.isArray(steps)) return false;
  const s = steps as Record<string, unknown>;
  return typeof s.spec_ts === "string" || typeof s.healed_spec_ts === "string" || s.format === "ir_v1";
}

// ─── Intent contract + gaps (plan_proposed / plan_updated) ───────────────────

/** Oracle families the planner can assert on, strongest evidence first (data > field > …). */
export type OracleKind =
  | "data"
  | "field"
  | "error"
  | "visibility"
  | "toast"
  | "related-list"
  | "text"
  | "url";

/** One checkable outcome the test must prove. */
export interface IntentContractOracle {
  kind: OracleKind;
  target?: string;
  expectation?: string;
}

/**
 * The planner's structured reading of what the user asked for — what object, what behaviour, as
 * whom, and how success is proven. Rendered next to the plan so the user can confirm the agent
 * understood the ask BEFORE approving. Paired with `intent_gaps`: the parts it could not pin down.
 */
export interface IntentContract {
  object?: string;
  behaviour: string;
  actor?: string;
  recordType?: string;
  oracle: IntentContractOracle[];
  requestedTargets?: string[];
}

// ─── Typed data queries (IR steps) ───────────────────────────────────────────

export type TypedQueryOperator = "=" | "!=" | ">" | "<" | ">=" | "<=" | "LIKE" | "IN";

export interface TypedQueryCondition {
  field: string;
  op: TypedQueryOperator;
  value: string | number | boolean | null | readonly string[];
}

/** Back-compat alias for the condition shape. */
export type TypedQueryFilter = TypedQueryCondition;

/**
 * Structured replacement for a hand-written `soql` string on a data step. The server compiles it,
 * so it can validate fields against the org schema instead of shipping an unparsed string — which
 * is why a `query` assertion scores as stronger evidence than the same assertion written as SOQL.
 * `limit` is mandatory server-side (capped at 200).
 */
export interface TypedQuery {
  sobject: string;
  select: readonly string[];
  where?: readonly TypedQueryCondition[];
  orderBy?: { field: string; direction?: "ASC" | "DESC" };
  limit: number;
}

/**
 * A data-assertion / query step inside an IR plan body. Legacy steps carry a raw `soql`; new steps
 * carry a `query`. Both are optional because either form may be present — read `query` first and
 * fall back to `soql`.
 */
export interface AssertDataStep {
  kind?: string;
  plain_english?: string;
  soql?: string;
  query?: TypedQuery;
  [key: string]: unknown;
}

/** Alias for the read/query flavour of the same shape (the server calls it a query op). */
export type QueryOp = AssertDataStep;

// ─── Self-heal proposal (plan body) ──────────────────────────────────────────

/** A single edit the healer wants to make to the plan's IR. Max 3 patches per proposal. */
export interface HealPatch {
  op: "replace" | "remove";
  /** Index into the ORIGINAL `ir.steps` array — all patches address original indices. */
  stepIndex: number;
  /** Required for `replace`, ignored for `remove`. Opaque IR step. */
  step?: Record<string, unknown>;
}

/**
 * A heal the service PROPOSED but did not apply. Lives on the plan body until the user accepts
 * (POST /api/plans/:planId/heal-proposal/accept) or rejects it. The source run's verdict is never
 * mutated — a failed run stays failed until an accepted heal is verified.
 */
export interface ProposedHeal {
  diagnosis: string;
  patches: readonly HealPatch[];
  /** The run that executed the PATCHED plan and PASSED — not the run that failed. */
  verification_run_id: string;
  created_at: string;
  /** Server sends the raw string; it is a TriageCategory in practice but not enum-validated. */
  triage_category: string;
  failing_step_index: number;
  source_run_id?: string | null;
}

/** Audit trail entry appended to the plan body each time a proposal is accepted. */
export interface HealRevision {
  applied_at: string;
  applied_by: string;
  diagnosis: string;
  patches: readonly HealPatch[];
  verification_run_id: string;
  source_run_id: string | null;
  triage_category: string;
}

/**
 * Fields the service writes onto a plan's `steps` body that the generated OpenAPI types do not
 * declare (`proposed_heal` / `heal_revisions` are runtime-only; the rest are declared but not all
 * mirrored into the Zod schema codegen reads). Kept as a standalone interface so `generated.ts` can
 * still be regenerated from openapi.json without losing them.
 *
 * NOTE the naming split: the wire event carries `intent_gaps`, the stored body calls the same data
 * `contract_gaps`.
 */
export interface PlanBodyExtras {
  contract?: IntentContract;
  contract_gaps?: string[];
  proposed_heal?: ProposedHeal;
  heal_revisions?: HealRevision[];
  confidence?: number;
  confidence_reasons?: string[];
  live_ui_fields?: Record<string, string[]>;
}

/** Narrow a plan body to the heal/contract extras the service writes onto it. */
export function planBodyExtras(steps: PlanBody | null | undefined): PlanBodyExtras {
  if (!steps || Array.isArray(steps)) return {};
  return steps as PlanBodyExtras;
}

/** The pending, un-accepted heal on a plan (if any). */
export function proposedHealOf(steps: PlanBody | null | undefined): ProposedHeal | null {
  return planBodyExtras(steps).proposed_heal ?? null;
}

// ─── Execute / heal result (SSE complete event payload) ───────────────────────

/** Failure triage categories produced by the LLM failure analyst. */
export type TriageCategory =
  | "SELECTOR_DRIFT"
  | "VIEW_RENDER_GAP"
  | "MISSING_SETUP_DATA"
  | "VALIDATION_RULE_BLOCK"
  | "AUTOMATION_MUTATED_VALUE"
  | "FLS_DENIED"
  | "OBJECT_PERMISSION_DENIED"
  | "TIMING_ASYNC"
  | "PAGE_LAYOUT_MISMATCH"
  | "AMBIGUOUS_EXPECTATION"
  | "REAL_REGRESSION"
  | "UNKNOWN";

export interface TriageResult {
  category: TriageCategory;
  confidence: "high" | "medium" | "low";
  reasoning: string;
  recommendation: string;
  retryRecommended: boolean;
}

export interface FailureQuestion {
  question: string;
  suggestedAction: string;
  blastRadiusSummary: string | null;
  failureCategory?: TriageCategory;
  rootCause?: string;
  evidence?: string[];
  recommendedChange?: string;
  requiresUserAnswer?: boolean;
}

export interface RepairQuestionEvent {
  type: "repair_question";
  run_id: string;
  plan_id: string;
  failure_category: TriageCategory;
  confidence: "high" | "medium" | "low";
  root_cause: string;
  evidence: string[];
  question: string;
  recommended_change: string;
  requires_user_answer: true;
}

/**
 * Outcome messaging — who a non-passing run's explanation is for, and what to do.
 * `audience: "user"` → an org/app issue the user can act on (show the reason +
 * action). `audience: "internal"` → our-code/runner issue (generic line; the real
 * detail goes to our logs/trace, never blaming the user's org).
 */
export interface OutcomeMessage {
  audience: "user" | "internal";
  cause: "org" | "app" | "our_code";
  headline: string;
  reason: string;
  action: string | null;
  /** Optional rich markdown explanation (what/why/how-to-fix, with a mermaid diagram for causal
   *  chains). When present it's rendered as the card body; reason/action are the plain fallback. */
  explanation?: string | null;
}

// ─── Per-run "what to test next" suggestions (GET /api/runs/:runId/suggestions) ─
export interface RunSuggestion {
  /** Short imperative label the user clicks. */
  title: string;
  /** The instruction seeded into chat to have the agent author this test. */
  chat_prompt: string;
  kind: "field" | "validation_rule" | "action";
}

// ── Regression suites (/api/suites) — snake_case, straight from the store rows ──
export interface TestSuite {
  id: string;
  name: string;
  description: string | null;
  plan_ids: string[];
  created_at: string;
  updated_at: string;
}
export type SuiteRunStatus =
  | "passed"
  | "passed_with_retry"
  | "degraded"
  | "blocked"
  | "failed"
  | "error";
export interface SuiteRunResult {
  plan_id: string;
  plan_name: string;
  status: SuiteRunStatus;
  run_id: string | null;
}
export interface TestSuiteRun {
  id: string;
  suite_id: string;
  status: "running" | "complete" | "error";
  total: number;
  passed: number;
  failed: number;
  blocked: number;
  errored: number;
  results: SuiteRunResult[];
  label: string | null;
  created_at: string;
  finished_at: string | null;
}
export interface SuiteDiffEntry {
  plan_id: string;
  plan_name: string;
  from: SuiteRunStatus | null;
  to: SuiteRunStatus | null;
}
export interface SuiteDiff {
  regressed: SuiteDiffEntry[];
  fixed: SuiteDiffEntry[];
  stillFailing: SuiteDiffEntry[];
  added: SuiteDiffEntry[];
  removed: SuiteDiffEntry[];
  noBaseline: boolean;
}

// ── Coverage dashboard (/api/plans/coverage/dashboard) ──
export interface CoverageCategory {
  key: string;
  label: string;
  total: number;
  covered: number;
  pct: number;
  uncovered: string[];
  /** False when the category couldn't be read (permission/API error) — show "unavailable", not a %. */
  available: boolean;
}
export interface CoverageDashboard {
  categories: CoverageCategory[];
  overallPct: number;
  summary: string;
}

/**
 * The run's TRUE outcome. `status` is only ever passed|failed, so anything amber — a run that only
 * went green after a timing retry, one that degraded, or one BLOCKED by an org/implementation issue
 * (a premise that doesn't hold, a permission limit, a field that exists but isn't on the layout) —
 * is invisible unless you read this. A BLOCKED run is NOT a test failure; render it as actionable
 * guidance, not a red error.
 */
export type ExecutionState =
  | "passed"
  | "passed_with_retry"
  | "degraded"
  | "blocked"
  | "failed";

/**
 * How much the run had to lean on recovery machinery to reach its verdict. A green run with a
 * non-zero budget is a flake signal, not a clean pass.
 */
export interface FlakeBudget {
  timing_retries: number;
  ai_recoveries: number;
  user_clarifications: number;
  passed_with_retry: boolean;
}

/** A step result, plus the retry flag the generated schema doesn't declare. */
export type RunStepResult = StepResult & {
  /** Passed only because Playwright retried it — a weaker pass; feeds `flake_budget`. */
  passed_with_retry?: boolean;
};

export interface ExecuteResult {
  run_id: string;
  /**
   * `error` is the terminal shape the execute route emits when it aborts the run (credentials,
   * compile failure, …) — `complete`, not `error`, is always the end-of-stream signal.
   */
  status: "passed" | "failed" | "error";
  /**
   * Mirror of `summary.execution_state`, lifted to the top level so a result adapted from a
   * persisted run (whose `status` collapses to passed|failed) still carries its amber state.
   */
  execution_state?: ExecutionState;
  duration_ms: number;
  step_results: RunStepResult[];
  summary: {
    passed: number;
    failed: number;
    overall: string;
    execution_state?: ExecutionState;
    /** Retry/recovery accounting behind the verdict — a non-zero budget on a pass means flaky. */
    flake_budget?: FlakeBudget;
    /** Teardown steps that did not complete — leftover test data the user may need to clean up. */
    cleanup_warnings?: string[];
    /** Raw-spec path only: how many attempts disagreed across the best-of-N verification. */
    flaky?: number;
    // Set after browser run
    first_failure?: StepResult | null;
    error?: string | null;
    report_html?: string;
    report_url?: string | null;
    records?: Array<Record<string, unknown>>;
    records_narrative?: string | null;
    console_tail?: string | null;
    // Set by the narration agent post-run
    narrative?: string | null;
    // Outcome messaging (non-passing runs): org/app issue → shown to the user with
    // a reason + action; our-code issue → generic, kept off the user's plate.
    outcome?: OutcomeMessage;
    user_message?: string;
    // Set by the failure triage agent (failed runs only)
    triage?: TriageResult;
    // Set by the question engine (failed runs only, after triage)
    question?: FailureQuestion;
    // Set when self-healing was attempted
    heal_diagnoses?: string[];
    needs_human?: boolean;
    // Set when IR was compiled with unknown fields
    compile_warnings?: string[];
    // Tokens the run's LLM calls spent (self-heal, triage, narration, repair question).
    token_usage?: { input: number; output: number; total: number; calls: number };
  };
}

export interface PlanContract {
  version: "plan_contract_v1";
  intent: string;
  actor: {
    mode: "connected_salesforce_user";
    persona: string;
  };
  prerequisites: string[];
  setup: Array<{
    operation: string;
    object?: string;
    ref?: string;
    id?: string;
    fields?: string[];
    soql?: string;
    description: string;
  }>;
  pages: Array<{
    page: string;
    object?: string;
    record?: string;
    name?: string;
    description: string;
  }>;
  ui_actions: Array<{
    kind: string;
    label: string;
    target?: string;
    description: string;
  }>;
  fields: Array<{
    label: string;
    object?: string;
    operation: "fill" | "read" | "assert" | "setup" | "edit";
    value?: string;
  }>;
  assertions: Array<{
    kind: string;
    target: string;
    expectation: string;
  }>;
  records: Array<{
    ref?: string;
    object?: string;
    source: "create" | "query" | "update" | "delete" | "reference";
    fields?: string[];
    soql?: string;
  }>;
  cleanup: Array<{
    operation: string;
    object?: string;
    ref?: string;
    id?: string;
    fields?: string[];
    soql?: string;
    description: string;
  }>;
  coverage: {
    objects: string[];
    fields: string[];
  };
  clarifications_required: string[];
  // "unverified" = proposed, but org metadata was unavailable during planning so
  // field/schema names could not be verified against the org (the run confirms).
  confidence: "ready" | "needs_clarification" | "unverified";
}

/**
 * `warning` = the step answered but the answer is not usable evidence (the tool was unavailable,
 * the agent abstained, the FLS graph wasn't populated). Distinct from `error` (the call failed) and
 * from `ok` (real evidence) — see `toolResultOutcome`.
 */
export type ActivityStatus = "running" | "ok" | "warning" | "error" | "blocked";

export interface RuntimeActivityEvent {
  type: "runtime_activity";
  id?: string;
  at?: string;
  source?: string;
  kind: "phase" | "metadata" | "schema" | "browser" | "salesforce" | "plan" | "evidence" | "question" | "error" | "ai_recovery" | "cleanup";
  label: string;
  detail?: string;
  status?: ActivityStatus;
  tool?: string;
  data?: unknown;
}

// ─── tool_result normalisation ───────────────────────────────────────────────
//
// `ok: true` on a batch-explore tool_result means "the call returned", NOT "we learned something".
// The payload can still say the tool was unavailable, that the agent abstained, or that the FLS
// graph has no data — all of which are amber, not green. Rendering those as successes is how a
// plan gets built on evidence that was never actually gathered.

/** Degenerate `ok: true` payload shapes emitted by the explore tools. */
export interface ToolResultData {
  unavailable?: boolean;
  reason?: string;
  error?: string;
  abstained?: boolean;
  anchor?: string;
  fls_graph_populated?: boolean;
  note?: string;
  [key: string]: unknown;
}

export interface ToolResultOutcome {
  status: ActivityStatus;
  /** Why it is amber, when it is — server-supplied wording, suitable for the activity detail line. */
  reason?: string;
}

/**
 * Collapse a `tool_result` (ok flag + payload) into the status the activity feed should show.
 * `ok: false` → error; `ok: true` with a degenerate payload → warning; otherwise → ok.
 */
export function toolResultOutcome(ok: boolean, data: unknown, error?: string | null): ToolResultOutcome {
  if (!ok) return { status: "error", reason: error ?? undefined };
  const d = (data && typeof data === "object" && !Array.isArray(data) ? data : {}) as ToolResultData;
  if (d.unavailable === true) {
    return { status: "warning", reason: d.reason ?? d.error ?? "tool unavailable" };
  }
  if (d.abstained === true) {
    return { status: "warning", reason: d.anchor ? `abstained (${d.anchor})` : "agent abstained" };
  }
  if (d.fls_graph_populated === false) {
    return { status: "warning", reason: d.note ?? "field-level security graph not populated" };
  }
  return { status: "ok" };
}

// ─── SSE event types ──────────────────────────────────────────────────────────

/** Events emitted by POST /api/chat/messages */
export type TestingChatEvent =
  | { type: "session"; session_id: string }
  | { type: "thinking"; phase?: string }
  | RuntimeActivityEvent
  | { type: "tool_call"; tool: string; args: Record<string, unknown>; say: string }
  // `ok: true` is NOT success — see `toolResultOutcome`.
  | { type: "tool_result"; tool: string; ok: boolean; data: ToolResultData | unknown; error: string | null }
  | {
      type: "plan_proposed";
      plan_id: string;
      name: string;
      steps: PlanBody;
      plan_contract?: PlanContract;
      /** The planner's structured reading of the ask. */
      intent_contract?: IntentContract;
      /** Parts of the ask the planner could not pin down — surface before approval. */
      intent_gaps?: string[];
      /** Destructive steps (delete/edit-existing) — gate the RUN when non-empty. */
      destructive_actions?: DestructiveAction[];
      updated?: boolean;
      explore_calls?: number;
      explore_rounds?: number;
    }
  | {
      type: "plan_updated";
      plan_id: string;
      name: string;
      steps: PlanBody;
      diff: Record<string, unknown>;
      requires_approval: boolean;
      plan_contract?: PlanContract;
      intent_contract?: IntentContract;
      intent_gaps?: string[];
      /** Destructive steps (delete/edit-existing) — gate the RUN when non-empty. */
      destructive_actions?: DestructiveAction[];
    }
  | { type: "assistant_message"; content: string; metadata: Record<string, unknown> }
  /** The planner decided to kick off a run itself — the UI should follow the plan into execution. */
  | { type: "start_run"; plan_id: string; headed: boolean }
  | { type: "usage"; input_tokens: number; output_tokens: number; total_tokens: number; calls: number }
  // NOTE the key asymmetry: chat in-stream errors use `error`; execute/heal use `message`.
  | { type: "error"; error: string }
  | { type: "done" };

// ─── Self-heal stream (POST /api/runs/:id/heal) ──────────────────────────────
//
// The heal stream PROPOSES; it never mutates the source run. A failed run stays failed. The stream
// ends with `complete` carrying status 'proposed' (a patch set awaiting accept/reject) or
// 'declined' (nothing worth proposing, with a reason) — it is NOT an ExecuteResult and there is no
// second verdict to render. When the feature is off, opening the stream fails with HTTP 410,
// surfaced as a TestingApiError with status 410.

export interface HealProposalStartedEvent {
  type: "heal_proposal_started";
  run_id: string;
  triage_category: TriageCategory;
}

export type HealStage =
  | "triage_gate"
  | "grounding"
  | "generating"
  | "validating"
  | "intent_gate"
  | "verifying"
  | "proposed"
  | "declined";

export interface HealProgressEvent {
  type: "heal_progress";
  stage: HealStage;
  attempt?: number;
  max_attempts?: number;
  detail?: string;
}

export interface HealProposalEvent {
  type: "heal_proposal";
  run_id: string;
  plan_id: string;
  proposal: ProposedHeal;
  accept_url: string;
  reject_url: string;
}

/**
 * Terminal heal event. `plan_id` and `notes` are optional because the early-exit form (triage
 * unavailable) omits both: `{ run_id, status: 'declined', reason: 'triage unavailable' }`.
 */
export interface HealCompleteEvent {
  type: "complete";
  run_id: string;
  plan_id?: string;
  status: "proposed" | "declined";
  /** Present when status is 'proposed'. */
  proposal?: ProposedHeal;
  /** Present when status is 'declined' — why no patch was worth proposing. */
  reason?: string;
  notes?: string[];
}

/** True for the heal stream's terminal event (as opposed to an execute run's ExecuteResult). */
export function isHealComplete(e: { type: string }): e is HealCompleteEvent {
  return (
    e.type === "complete" &&
    ((e as HealCompleteEvent).status === "proposed" ||
      (e as HealCompleteEvent).status === "declined")
  );
}

/**
 * Alias for the heal stream's event union. Heal and execute share the transport and most events
 * (runtime_activity, progress, error, …), so this is the same union — the difference is which
 * members actually appear and what `complete` means (see `isHealComplete`).
 */
export type HealStreamEvent = ExecuteStreamEvent;

/** Events emitted by POST /api/plans/:id/execute and POST /api/runs/:id/heal */
export type ExecuteStreamEvent =
  | { type: "smoke_check" }
  | { type: "smoke_failed"; error: string }
  | { type: "run_started"; run_id: string }
  | { type: "test_result"; title: string; status: "passed" | "failed"; duration_ms: number; error?: string }
  | { type: "browser_complete"; status: string; passed: number; failed: number }
  // Emitted on a `?mode=replay` deterministic re-run: did the plan still hold?
  | {
      type: "replay_verdict";
      verdict: "passed" | "drift" | "regression";
      mode: "replay" | "author";
      execution_state: ExecutionState | null;
      validated: boolean;
      confidence?: "high" | "medium" | "low";
      confidence_score?: number;
      confidence_reasons?: string[];
    }
  | { type: "narrating" }
  | { type: "triaging" }
  | HealProposalStartedEvent
  | HealProgressEvent
  | HealProposalEvent
  | HealCompleteEvent
  | { type: "questioning" }
  | RuntimeActivityEvent
  | RepairQuestionEvent
  // Outcome explanation (non-passing runs): structured org-vs-our-code messaging.
  // Also carried on the terminal `complete` event as summary.outcome.
  | {
      type: "outcome_explanation";
      run_id: string;
      plan_id: string;
      audience: "user" | "internal";
      cause: "org" | "app" | "our_code";
      headline: string;
      reason: string;
      action: string | null;
    }
  // Live telemetry streamed while the browser runs (drives the Live Browser view).
  | { type: "progress"; elapsed_ms: number; phase: string }
  | { type: "step"; phase: "begin" | "end"; title: string; ok?: boolean; error?: string }
  | { type: "api"; method: string; resource: string; status: number | string }
  | { type: "screenshot"; label: string; jpeg_base64: string }
  | ({ type: "complete" } & ExecuteResult)
  | { type: "error"; message: string };
